import React, { useState, useEffect, useRef } from 'react';
import { X, User, Hash, Camera, Image, QrCode } from 'lucide-react';
import { Apartment, ApartmentStatus } from '../types/building';
import { statusLabels } from '../constants/statusConfig';
import { getApartmentLabel } from '../utils/getApartmentLabel';
import { TechnicianSelector } from './TechnicianSelector';
import { useLocalAuth } from '../hooks/useLocalAuth';
import { KonzernIdSelector } from './KonzernIdSelector';
import { getAvailableKonzernIds } from '../lib/localDatabase';
import { PhotoGallery } from './PhotoGallery';
import { PhotoUploader } from './PhotoUploader';
import { BarcodeScanner } from './BarcodeScanner';

interface ApartmentDialogProps {
  apartment: Apartment;
  onClose: () => void;
  onSave: (updatedApartment: Apartment) => void;
}

/**
 * Dialog zur Bearbeitung einer Wohnung/eines Kellerbereichs
 */
export const ApartmentDialog: React.FC<ApartmentDialogProps> = ({
  apartment,
  onClose,
  onSave,
}) => {
  const [status, setStatus] = useState<ApartmentStatus>(apartment.status);
  const [notes, setNotes] = useState(apartment.notes);
  const [abortReason, setAbortReason] = useState(apartment.abortReason || '');
  const [technician, setTechnician] = useState(apartment.technician || '');
  const [konzernId, setKonzernId] = useState(apartment.konzernId || '');
  const [availableKonzernIds, setAvailableKonzernIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'details' | 'photos'>('details');
  const [showPhotoUploader, setShowPhotoUploader] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const { user } = useLocalAuth();

  useEffect(() => {
    // Load available Konzern IDs
    const konzernIds = getAvailableKonzernIds();
    setAvailableKonzernIds(konzernIds.map(kid => kid.value));
    
    // If the apartment already has a Konzern ID, add it to the list
    if (apartment.konzernId && !konzernIds.some(kid => kid.value === apartment.konzernId)) {
      setAvailableKonzernIds(prev => [...prev, apartment.konzernId!]);
    }
  }, [apartment.konzernId]);

  const handleSave = () => {
    onSave({
      ...apartment,
      status,
      notes,
      abortReason: status === 'aborted' ? abortReason : undefined,
      technician: technician || user?.name || '',
      konzernId: konzernId || undefined,
      lastUpdated: new Date(),
    });
    onClose();
  };

  const handleScanComplete = (scannedValue: string) => {
    // Check if the scanned value looks like an apartment number
    if (/^\d{3}-\d{3}$/.test(scannedValue)) {
      // This is likely an apartment number, not a Konzern ID
      // You could handle this differently if needed
      alert('Der gescannte Code scheint eine Wohnungsnummer zu sein, nicht eine Konzern ID.');
    } else {
      // Assume it's a Konzern ID
      setKonzernId(scannedValue);
      
      // If this is a new Konzern ID that's not in the available list, add it
      if (!availableKonzernIds.includes(scannedValue)) {
        setAvailableKonzernIds(prev => [...prev, scannedValue]);
      }
    }
    
    setShowScanner(false);
  };

  const apartmentLabel = getApartmentLabel(apartment);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-800 break-words">
            {apartmentLabel} {apartment.formattedNumber}
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b mb-6">
          <button
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === 'details'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('details')}
          >
            Details
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm flex items-center gap-1 ${
              activeTab === 'photos'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('photos')}
          >
            <Image className="w-4 h-4" />
            <span>Fotos</span>
          </button>
        </div>

        {activeTab === 'details' ? (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as ApartmentStatus)}
                className="w-full p-2.5 bg-white border border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              >
                {Object.entries(statusLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {status === 'aborted' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Abbruchgrund
                </label>
                <input
                  type="text"
                  value={abortReason}
                  onChange={(e) => setAbortReason(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  placeholder="Grund für den Abbruch eingeben..."
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Techniker
              </label>
              <TechnicianSelector 
                value={technician}
                onChange={setTechnician}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center justify-between">
                <span>Konzern ID</span>
                <button
                  onClick={() => setShowScanner(true)}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                >
                  <QrCode className="w-3.5 h-3.5" />
                  <span>Scannen</span>
                </button>
              </label>
              <KonzernIdSelector
                value={konzernId}
                onChange={setKonzernId}
                availableIds={availableKonzernIds}
                currentId={apartment.konzernId}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notizen
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full p-2.5 border border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 h-32 resize-none"
                placeholder="Notizen zur Installation..."
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Speichern
              </button>
            </div>
          </div>
        ) : (
          <div>
            {showPhotoUploader ? (
              <PhotoUploader
                buildingId={apartment.buildingId || ''}
                apartmentId={apartment.id}
                onUploadComplete={() => setShowPhotoUploader(false)}
                onCancel={() => setShowPhotoUploader(false)}
              />
            ) : (
              <PhotoGallery
                buildingId={apartment.buildingId || ''}
                apartmentId={apartment.id}
                onAddPhotos={() => setShowPhotoUploader(true)}
              />
            )}
          </div>
        )}
      </div>

      {showScanner && (
        <BarcodeScanner
          onScan={handleScanComplete}
          onClose={() => setShowScanner(false)}
          title="Konzern ID scannen"
          description="Scannen Sie den Barcode oder QR-Code der Konzern ID"
        />
      )}
    </div>
  );
};